// Helper functions for checking resource types
import axios from 'axios';

// Laravel API URL - use relative path (proxy handles dev, same origin handles prod)
const LARAVEL_API_URL = '/api';

/**
 * Check if a resource slug is a group or lesson
 * @param {string} subjectSlug - Subject slug
 * @param {string} chapterSlug - Chapter slug
 * @param {string} resourceSlug - The slug to check (could be group or lesson)
 * @param {string} parentGroupSlug - Optional parent group slug for nested checks
 * @returns {Promise<{isGroup: boolean, isLesson: boolean, depth: number}>}
 */
async function checkResourceType(subjectSlug, chapterSlug, resourceSlug, parentGroupSlug = null) {
  try {
    const response = await axios.post(LARAVEL_API_URL + '/quiz-app/library/check-resource-type', {
      slug: subjectSlug,
      chapter_slug: chapterSlug,
      resource_slug: resourceSlug,
      parent_group_slug: parentGroupSlug
    }, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.error('Error checking resource type:', error);
    return { isGroup: false, isLesson: false, isTopicGroup: false };
  }
}

// Legacy function for backward compatibility
async function checkIfTopicGroup(params) {
  const result = await checkResourceType(
    params.slug,
    params.chapter_slug,
    params.topic_group_slug
  );
  return { isTopicGroup: result.isGroup || result.isTopicGroup };
}

const routes = [
  {
    path: "/",
    component: () => import(/* webpackChunkName: "layout-main" */ "layouts/MainLayout.vue"),

    children: [
      {
        path: "",
        component: () => import(/* webpackChunkName: "page-index" */ "pages/IndexPage.vue"),
        name: "IndexPage",
      },

      {
        path: "/createtest",
        component: () => import(/* webpackChunkName: "page-createtest" */ "pages/createtest.vue"),
        name: "createtest",
      },
      {
        path: "/fastmode",
        component: () => import(/* webpackChunkName: "page-fastmode" */ "pages/fastmode.vue"),
        name: "fastmodePage",
      },
      {
        path: "/previoustests",
        component: () => import(/* webpackChunkName: "page-previoustests" */ "pages/previoustestsPage.vue"),
        name: "previoustests",
      },
      {
        path: "/freeforms",
        component: () => import(/* webpackChunkName: "page-freeforms" */ "pages/freeformsPage.vue"),
        name: "freeformsPage",
      },
      {
        path: "/previoustests/:quizID",
        component: () => import(/* webpackChunkName: "page-previoustests-single" */ "pages/previoustestsPage.vue"),
        name: "previoustestsSignleQuiz",
      },
      {
        path: "/previoustests/results/:quizID",
        component: () => import(/* webpackChunkName: "page-results-prev-tests" */ "pages/results_prevTestsPage.vue"),

        // hey, it has children routes and User has <router-view> in it;
        // It is really a Layout then!
        children: [
          {
            path: "", // here it is, route /user/profile
            redirect: { name: "testresults" },
          },
          // Profile page
          {
            path: "testresults", // here it is, route /user/profile
            name: "testresults",
            component: () =>
              import(/* webpackChunkName: "page-test-results" */ "pages/results_testResults_prevTestsPage.vue"), // we reference /src/pages/Profile.vue imported above
          },

          // Posts page
          {
            path: "testanalysis", // here it is, route /user/posts
            name: "testanalysis",

            component: () =>
              import(/* webpackChunkName: "page-test-analysis" */ "pages/results_testAnalysis_prevTestsPage.vue"), // we reference /src/pages/Posts.vue imported above
          },

          // Group Study page (collaborative quiz results)
          {
            path: "groupstudy",
            name: "groupstudy",
            component: () =>
              import(/* webpackChunkName: "page-group-study" */ "pages/results_groupStudy_prevTestsPage.vue"),
          },
        ],
      },
      {
        path: "/flashcards",
        component: () => import(/* webpackChunkName: "page-flashcards" */ "pages/flashcards.vue"),
        name: "flashcards",
      },
      {
        path: "/flashcardsview/:flashid",
        component: () => import(/* webpackChunkName: "page-flashcard-view" */ "pages/signle_flashcard.vue"),
        name: "signleflashcardPage",
      },
      {
        path: "/myflashcards",
        component: () => import(/* webpackChunkName: "page-my-flashcards" */ "pages/myflashcardsPage.vue"),
        name: "myflashcards",
      },

      {
        path: "/osce",
        component: () => import(/* webpackChunkName: "page-osce-browse" */ "pages/osce/OsceBrowse.vue"),
        name: "osceBrowse",
      },
      {
        path: "/osce/station/:stationId",
        component: () => import(/* webpackChunkName: "page-osce-station" */ "pages/osce/OsceStationPlayer.vue"),
        name: "osceStationView",
      },
      {
        path: "/osce/mini/:runId",
        component: () => import(/* webpackChunkName: "page-osce-mini-run" */ "pages/osce/osceMiniRunPage.vue"),
        name: "osceMiniRun",
      },
      {
        path: "/osce/partner/:sessionId",
        component: () => import(/* webpackChunkName: "page-osce-partner" */ "pages/osce/OscePartnerPage.vue"),
        name: "oscePartner",
      },

      {
        path: "/performance",

        // hey, it has children routes and User has <router-view> in it;
        // It is really a Layout then!
        children: [
          {
            path: "", // here it is, route /user/profile
            redirect: { name: "performancePage_overall" },
          },
          // Profile page
          {
            path: "overall", // here it is, route /user/profile
            name: "performancePage_overall",
            component: () => import(/* webpackChunkName: "page-performance-overall" */ "pages/performancePage_overall.vue"), // we reference /src/pages/Profile.vue imported above
          },

          // Posts page
          {
            path: "reports", // here it is, route /user/posts
            name: "performancePage_reports",

            component: () => import(/* webpackChunkName: "page-performance-reports" */ "pages/performancePage_reports.vue"), // we reference /src/pages/Posts.vue imported above
          },

          // Performance graphs (UWorld-style score-over-time / by-test charts)
          {
            path: "graphs",
            name: "performancePage_graphs",
            component: () => import(/* webpackChunkName: "page-performance-graphs" */ "pages/performancePage_graphs.vue"),
          },
        ],
      },

      {
        path: "/reset",
        component: () => import(/* webpackChunkName: "page-reset" */ "pages/resetPage.vue"),
        name: "resetPage",
      },
      {
        path: "/help",
        component: () => import(/* webpackChunkName: "page-help" */ "pages/helpPage.vue"),
        name: "helpPage",
      },
      {
        path: "/notes",
        component: () => import(/* webpackChunkName: "page-notes" */ "pages/notesPage.vue"),
        name: "notesPage",
      },
      {
        path: "/search",
        component: () => import(/* webpackChunkName: "page-search" */ "pages/searchPage.vue"),
        name: "searchPage",
      },
      {
        path: "/library",
        component: () => import(/* webpackChunkName: "page-library-main" */ "pages/library/main_library.vue"),
        name: "main_library",
      },

      // NOTE: library search is NOT a standalone route — it is an IN-PLACE mode of the lesson
      // shell (lesson_view renders LibrarySearchPanel in the content pane when the current
      // route carries ?q=), so the TOC + command header persist, UWorld-style.

      // IMPORTANT: Order matters! More specific routes first (longer paths first)

      // Interactive lecture (Lecture Studio, 2026-09-18): the lecture lives UNDER the
      // lesson's own URL — /library/…/{lesson}/lecture — one route per lesson depth.
      // The page resolves the lesson by its path and frames the Laravel player
      // (/lectures/{id}) full-screen, so the address bar keeps the lesson's URL and
      // the app's own router owns the way back. A static trailing segment outranks a
      // param at the same position in vue-router 4, so `…/lecture` never falls into
      // the lesson / group routes below (and never shadows them).
      {
        path: "/library/:slug/:chapter_slug/:topic_slug/:subtopic_slug/:subsubtopic_slug/:lesson_slug/lecture",
        component: () => import(/* webpackChunkName: "page-library-lecture" */ "pages/library/lecture_view.vue"),
        name: "lecture_view_deep",
      },
      {
        path: "/library/:slug/:chapter_slug/:topic_slug/:subtopic_slug/:resource_slug/lecture",
        component: () => import(/* webpackChunkName: "page-library-lecture" */ "pages/library/lecture_view.vue"),
        name: "lecture_view_nested",
      },
      {
        path: "/library/:slug/:chapter_slug/:topic_group_slug/:resource_slug/lecture",
        component: () => import(/* webpackChunkName: "page-library-lecture" */ "pages/library/lecture_view.vue"),
        name: "lecture_view_tagged",
      },
      {
        path: "/library/:slug/:chapter_slug/:lesson_slug/lecture",
        component: () => import(/* webpackChunkName: "page-library-lecture" */ "pages/library/lecture_view.vue"),
        name: "lecture_view",
      },

      // Deep nested lesson route (6 segments) - subject/chapter/topic/subtopic/subsubtopic/lesson
      {
        path: "/library/:slug/:chapter_slug/:topic_slug/:subtopic_slug/:subsubtopic_slug/:lesson_slug",
        component: () => import(/* webpackChunkName: "page-library-lesson-deep" */ "pages/library/lesson_view.vue"),
        name: "deep_nested_lesson_view",
        props: route => ({
          ...route.params,
          isTagged: true,
          nestingDepth: 4
        })
      },

      // 5 segments route - could be sub-subtopic GROUP view OR nested LESSON view
      // Uses resource_resolver to dynamically determine component based on API check
      {
        path: "/library/:slug/:chapter_slug/:topic_slug/:subtopic_slug/:resource_slug",
        component: () => import(/* webpackChunkName: "page-library-resolver-5" */ "pages/library/resource_resolver.vue"),
        name: "nested_resource_view",
        props: route => ({
          ...route.params,
          depth: 4
        })
      },

      // 4 segments route - could be subtopic GROUP view OR tagged LESSON view
      // Uses resource_resolver to dynamically determine component based on API check
      {
        path: "/library/:slug/:chapter_slug/:topic_group_slug/:resource_slug",
        component: () => import(/* webpackChunkName: "page-library-resolver-4" */ "pages/library/resource_resolver.vue"),
        name: "topic_resource_view",
        props: route => ({
          ...route.params,
          depth: 3
        })
      },

      // Topic group view route (3 segments after subject) - can be topic GROUP or lesson
      // Uses nested_group_view for groups to support nested structure
      {
        path: "/library/:slug/:chapter_slug/:topic_group_slug",
        component: () => import(/* webpackChunkName: "page-library-nested-group" */ "pages/library/nested_group_view.vue"),
        name: "topic_group_view",
        beforeEnter: async (to, from, next) => {
          // Check if it's actually a topic group
          try {
            const response = await checkIfTopicGroup(to.params);
            if (response.isTopicGroup) {
              // Set depth for nested_group_view
              to.params.depth = 2;
              to.params.groupSlug = to.params.topic_group_slug;
              next();
            } else {
              // Redirect to lesson view
              next({
                name: 'lesson_view',
                params: {
                  slug: to.params.slug,
                  chapter_slug: to.params.chapter_slug,
                  lesson_slug: to.params.topic_group_slug
                }
              });
            }
          } catch (error) {
            next({
              name: 'chapter_view',
              params: {
                slug: to.params.slug,
                chapter_slug: to.params.chapter_slug
              }
            });
          }
        },
        props: route => ({
          ...route.params,
          depth: 2,
          groupSlug: route.params.topic_group_slug
        })
      },

      // Lesson route (3 segments) - subject/chapter/lesson (no nesting)
      {
        path: "/library/:slug/:chapter_slug/:lesson_slug",
        component: () => import(/* webpackChunkName: "page-library-lesson" */ "pages/library/lesson_view.vue"),
        name: "lesson_view",
        props: route => ({
          ...route.params,
          isTagged: false,
          nestingDepth: 1
        })
      },

      // Chapter view route
      {
        path: "/library/:slug/:chapter_slug",
        component: () => import(/* webpackChunkName: "page-library-chapter" */ "pages/library/chapter_view.vue"),
        name: "chapter_view",
      },

      {
        path: "/library/:slug",
        component: () => import(/* webpackChunkName: "page-library-subject" */ "pages/library/subject_view.vue"),
        name: "subject_view",
      },

      {
        path: "/studyPlanner",
        component: () => import(/* webpackChunkName: "page-study-planner" */ "pages/StudyPlannerWizard.vue"),
        name: "studyPlanner",
      },
      {
        path: "/StudyPlanCalendar",
        component: () => import(/* webpackChunkName: "page-study-calendar" */ "pages/StudyPlanCalendar.vue"),
        name: "StudyPlanCalendar",
      },

      // Collaborative Quiz Routes
      {
        path: "/collaborative/lobby/:sessionId",
        component: () => import(/* webpackChunkName: "page-collaborative-lobby" */ "pages/collaborativeQuizLobby.vue"),
        name: "collaborativeQuizLobby",
      },
      {
        path: "/collaborative/results/:sessionId",
        component: () => import(/* webpackChunkName: "page-collaborative-results" */ "pages/collaborativeQuizResults.vue"),
        name: "collaborativeQuizResults",
      },
    ],
  },

  {
    path: "/qbanktestinterface/:quizID",
    component: () => import(/* webpackChunkName: "layout-qbank" */ "layouts/qbanktestinterface.vue"),
    name: "qbanktestinterfaceLayout",
    children: [
      {
        path: "",
        component: () => import(/* webpackChunkName: "page-qbank-interface" */ "pages/qbanktestinterfacePage.vue"),
        name: "qbanktestinterfacePage",
      },
    ],
  },

  // Always leave this as last one,
  // but you can also remove it
  {
    path: "/:catchAll(.*)*",
    component: () => import(/* webpackChunkName: "page-error-404" */ "pages/ErrorNotFound.vue"),
  },
];

export default routes;
